import { type Extent } from 'ol/extent';
import qs from 'qs';
import { type IMapLayerWfsOptions, type WfsVersion } from '../MapLayer.ts';
import { parseLayerAndOptions } from '../rendering/layerUrl.ts';

export const WfsDefaultVersion: WfsVersion = '2.0.0';

/** CRS della mappa: le geometrie vengono richieste già riproiettate. */
export const WfsDefaultSrsName = 'EPSG:3857';

export interface IWfsGetFeatureUrlOptions {
  baseUrl: string;
  wfs: IMapLayerWfsOptions;
  /**
   * BBox espresso nel CRS di `srsName`; se presente aggiunge il filtro
   * spaziale KVP `bbox` (unico filtro spaziale usato: l'intersezione
   * precisa viene calcolata client-side, come per ArcGIS).
   */
  extent?: Extent;
  srsName?: string;
}

/**
 * Costruisce l'URL KVP di una richiesta GetFeature WFS, preservando gli
 * eventuali parametri già configurati nell'URL (es. `authkey`).
 */
export function buildWfsGetFeatureUrl(
  options: IWfsGetFeatureUrlOptions,
): string {
  const { baseUrl, wfs, extent, srsName = WfsDefaultSrsName } = options;
  const { url, options: existingParams } = parseLayerAndOptions(baseUrl);
  const version = wfs.version ?? WfsDefaultVersion;

  const params: Record<string, unknown> = {
    ...existingParams,
    service: 'WFS',
    request: 'GetFeature',
    version,
    // WFS 2.0.0 ha rinominato il parametro `typeName` in `typeNames`
    [version === '2.0.0' ? 'typeNames' : 'typeName']: wfs.typeName,
    srsName,
  };

  if (wfs.outputFormat) {
    params.outputFormat = wfs.outputFormat;
  }

  if (extent) {
    // Il quinto componente esplicita il CRS del bbox ed evita le
    // ambiguità di ordine degli assi tra le versioni WFS
    params.bbox = `${extent.join(',')},${srsName}`;
  }

  return `${url}?${qs.stringify(params)}`;
}
